/* File: src\components\ProfileDashboard.js */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useProfile } from '../contexts/ProfileContext';
import { useGame } from '../contexts/GameContext';
import './animations.css';
import { useNavigate, Link } from 'react-router-dom'; // Link is already imported
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCirclePlay, faForward, faChartLine, faList, faArrowRight, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons'; // Import faExternalLinkAlt

// --- Import AudioManager ---
import audioManager from '../utils/audioManager';

// --- Import dashboard sub-components ---
import FocusMeter from './dashboard/FocusMeter';
import TodaysStats from './dashboard/TodaysStats';
import WeeklyStats from './dashboard/WeeklyStats';
import MonthlyStats from './dashboard/MonthlyStats';
import DailyPerformanceChart from './dashboard/DailyPerformanceChart'; // Updated component
import SessionsTable from './dashboard/SessionsTable';
import WeeklyTracker from './dashboard/WeeklyTracker';
import { formatCombinedDifficulty } from '../contexts/gameHelpers';

// --- Import date utils ---
// Keep these if needed for streak calculation, etc.
import { isDateToday, isDateYesterday, isSameDay, differenceInDays } from '../utils/dateUtils';
import { getWeeklyCompletionCount } from '../utils/weeklyTrackerUtils';

// --- Import a Loading Indicator ---
import LoadingSpinner from './ui/LoadingSpinner';

// --- Import useSettings ---
// Make sure useSettings is imported if not already
import { useSettings } from '../contexts/SettingsContext'; // Needed for timezone and audio preload

const MAX_NBACK = 9; // Should match backend if applicable
const DEFAULT_TIMEZONE = 'UTC'; // Fallback timezone

/**
 * ProfileDashboard Component - Main content for the dashboard route.
 * Displays profile info, stats, session history, and actions.
 * Coordinates starting a game and navigating to the game screen.
 * Includes logic to resume AudioContext on user interaction for mobile compatibility.
 * *** Uses user's timezone for the DailyPerformanceChart. ***
 */
const ProfileDashboard = () => {
    // --- HOOKS ---
    const {
        activeProfile,
        isLoading: isLoadingProfileContext, // Use the consolidated loading flag
    } = useProfile();
    const { startGame, setSaveToProfile, exitChallenge, challengeState, gameState, setNavigationHandler } = useGame();
    const navigate = useNavigate();
    // --- Get Settings (for timezone and audio preload) ---
    const { settings, isLoading: isLoadingSettings } = useSettings(); // Assuming useSettings provides settings object

    // Effect to set navigation handler for GameContext
    useEffect(() => {
        if (setNavigationHandler) {
            setNavigationHandler(navigate);
        }
        return () => {
            if (setNavigationHandler) {
                setNavigationHandler(null); // Clean up
            }
        };
    }, [setNavigationHandler, navigate]);


    // --- State for Stats Tabs ---
    const [activeStatsTab, setActiveStatsTab] = useState('today'); // 'today', 'week', 'month'

    // --- State for Performance Chart Period ---
    const [performanceChartPeriod, setPerformanceChartPeriod] = useState('today'); // 'today', 'week', 'month'

    // --- User Timezone ---
    // Get timezone from settings context, fallback to UTC
    const userTimezone = useMemo(() => {
        // Ensure settings object and timezone property exist
        return settings?.timezone || DEFAULT_TIMEZONE;
    }, [settings?.timezone]);


    // --- Streak Calculation (Memoized - No change needed here) ---
    const dailyStreak = useMemo(() => {
        // Logic remains the same, uses browser local time via isDateToday/isDateYesterday
        if (!activeProfile?.sessions || activeProfile.sessions.length === 0) return 0;
        const validSessions = activeProfile.sessions
            .map(s => ({ ...s, dateObj: s.date ? new Date(s.date) : null }))
            .filter(s => s.dateObj instanceof Date && !isNaN(s.dateObj));
        if (validSessions.length === 0) return 0;
        const sortedSessions = validSessions.sort((a, b) => b.dateObj - a.dateObj);
        const mostRecentSessionDate = sortedSessions[0].dateObj;

        // Use isDateToday/isDateYesterday which rely on the system's local time for streak logic
        const today = new Date();
        const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);

        const isRecent = isSameDay(mostRecentSessionDate, today) || isSameDay(mostRecentSessionDate, yesterday);

        if (!isRecent) {
            return 0; // Not today or yesterday
        }

        let currentStreak = 1; // Start at 1 if the most recent was today or yesterday
        let lastUniqueDayProcessed = mostRecentSessionDate;

        for (let i = 1; i < sortedSessions.length; i++) {
            const sessionDate = sortedSessions[i].dateObj;
            // Skip sessions from the same day we already counted
            if (isSameDay(sessionDate, lastUniqueDayProcessed)) {
                continue;
            }
            // Calculate the difference in days
            const dayDiff = differenceInDays(sessionDate, lastUniqueDayProcessed);
            // If the difference is exactly 1 day, it's a consecutive day
            if (dayDiff === 1) {
                currentStreak++;
                lastUniqueDayProcessed = sessionDate; // Update the last day processed
            } else {
                // If the difference is not 1, the streak is broken
                break;
            }
        }
        return currentStreak;
    }, [activeProfile?.sessions]);


    // --- CALLBACKS ---
    const handleStandardPlay = useCallback(async () => {
        // Standard play logic remains the same, uses settings?.selectedVoiceId
        if (!activeProfile) { console.warn("[ProfileDashboard] Cannot start game: Active profile not loaded."); return; }
        console.log("[ProfileDashboard] 'Start Training' clicked. Attempting to resume audio context...");
        try { audioManager.init(); const audioResumed = await audioManager.resume(); if (audioResumed) { console.log("[ProfileDashboard] Audio context resumed successfully."); const voiceId = settings?.selectedVoiceId || Object.keys(audioManager.VOICES)[0]; audioManager.preload({ voiceId: voiceId, difficulties: [0.05] }); } else { console.warn("[ProfileDashboard] Audio context could NOT be resumed via button click."); }
        } catch (error) { console.error("[ProfileDashboard] Error occurred while attempting to resume audio context:", error); }
        console.log("[ProfileDashboard] Proceeding with standard game start logic...");
        if (challengeState.isActive) { console.log("[ProfileDashboard] Exiting active challenge before starting standard game."); exitChallenge(); }
        setSaveToProfile(true);
        startGame({ nBack: activeProfile.currentNBack, difficulty: activeProfile.currentDifficulty });
    }, [activeProfile, challengeState.isActive, exitChallenge, setSaveToProfile, startGame, settings?.selectedVoiceId]); // Added settings dependency

    const handleChallenge = useCallback(() => {
        // Challenge logic remains the same
         if (!activeProfile || activeProfile.currentNBack >= MAX_NBACK) { console.warn("[ProfileDashboard] Cannot start challenge: Profile not loaded or already at max N-Back."); return; }
        const nBackToChallenge = activeProfile.currentNBack;
        console.log(`[ProfileDashboard] Navigating to challenge explanation for N=${nBackToChallenge}...`);
        navigate('/challenge-start', { state: { nBackToChallenge: nBackToChallenge } });
    }, [activeProfile, navigate]);

    const handlePerformanceChartPeriodChange = useCallback((period) => {
        setPerformanceChartPeriod(period);
    }, []);


    // Effect to handle navigation after game start initiated
    useEffect(() => {
        if (gameState === 'preparing') {
            console.log("[ProfileDashboard Effect] Game state is 'preparing', navigating to /game...");
            navigate('/game');
        }
    }, [gameState, navigate]);


    // --- CONDITIONAL LOADING RENDER ---
    // Use the consolidated isLoading flag from ProfileContext and add settings loading
    const isDashboardLoading = isLoadingProfileContext || isLoadingSettings; // Combine loading states

    if (isDashboardLoading) {
        return (
             <div className="w-full max-w-5xl mx-auto px-1 sm:px-4 md:px-6 py-3 sm:py-6 flex justify-center items-center h-screen -mt-16">
                 <div className="text-center">
                     {/* Ensure LoadingSpinner component is correctly imported and used */}
                     <LoadingSpinner message="Loading dashboard..." />
                 </div>
             </div>
         );
    }
    // Check if profile exists AFTER loading is complete
    if (!activeProfile || !activeProfile.id) {
         return (
              <div className="w-full max-w-5xl mx-auto px-1 sm:px-4 md:px-6 py-3 sm:py-6 flex justify-center items-center h-screen -mt-16">
                  <div className="text-center text-gray-600">
                      <p>No active profile found.</p>
                      <Link to="/profile-selection" className="text-indigo-600 hover:underline mt-2 inline-block">
                          Select or Create a Profile
                      </Link>
                  </div>
              </div>
          );
    }
    // --- END CONDITIONAL LOADING RENDER ---


    // --- DATA IS LOADED - RENDER DASHBOARD ---
    const currentNBackLevel = activeProfile.currentNBack || 2;
    const canChallenge = currentNBackLevel < MAX_NBACK;
    const challengeLevelDisplay = canChallenge ? formatCombinedDifficulty(currentNBackLevel, 0.50) : '-';

    // --- Highest Achieved Logic (remains the same) ---
    const highestAchievedNBackGood = activeProfile.highestNBackGood; // The N-back level achieved with 'Good' score (or null)
    const highestAchievedGoodSessionId = activeProfile.highestAchievedGoodSessionId; // The ID of the qualifying session (or null)
    const highestAchievedDisplayText = highestAchievedNBackGood
        ? `${highestAchievedNBackGood}-back`
        : ''; // Will be empty if no 'Good' score achieved yet
    const canLinkToReplay = !!highestAchievedGoodSessionId; // Enable button only if we have a session ID
    const showHighestAchieved = highestAchievedNBackGood !== null && highestAchievedNBackGood !== undefined; // Condition to show the line
    // --- End Highest Achieved Logic ---

    const currentLevelDisplay = formatCombinedDifficulty(activeProfile.currentNBack, activeProfile.currentDifficulty);
    const sessionsForComponents = activeProfile.sessions || []; // Provide default empty array
    // userTimezone is derived above using useMemo and useSettings


    return (
        // Main container
        <div className="w-full max-w-5xl mx-auto px-1 sm:px-4 md:px-6 py-3 sm:py-6">

            {/* Header Section: Profile Info, Streak, Focus Meter */}
             <div className="mb-5 sm:mb-8">
                 {/* Profile Info Card (Top Part) */}
                 <div className="flex flex-col sm:flex-row justify-between sm:items-center bg-white p-3 sm:p-5 rounded-t-lg border border-gray-200 shadow-sm border-b-0">
                     {/* Left Side: Avatar and Welcome */}
                     <div className="flex items-center mb-3 sm:mb-0">
                         <div className="h-10 w-10 sm:h-12 sm:w-12 bg-indigo-600 rounded-full flex items-center justify-center text-white text-lg sm:text-xl font-semibold mr-3 sm:mr-4 shadow-inner flex-shrink-0">
                             {activeProfile.username ? activeProfile.username.charAt(0).toUpperCase() : "U"}
                         </div>
                         <div className="overflow-hidden">
                            <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-800 truncate" title={activeProfile.username}>
                                Welcome, {activeProfile.username || 'User'}!
                            </h1>
                            {/* Highest Achieved Display - now conditional */}
                             {showHighestAchieved && (
                                 <div className="text-xs sm:text-sm text-gray-500">
                                    Highest Achieved:
                                    {canLinkToReplay ? (
                                        <Link
                                            to={`/replay/${highestAchievedGoodSessionId}`}
                                            className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-indigo-700 bg-indigo-50 hover:bg-indigo-100 font-semibold transition-colors duration-150 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:ring-offset-1"
                                            title={`Replay your first game at ${highestAchievedDisplayText} with a 'Good' score or better`}
                                        >
                                            {highestAchievedDisplayText}
                                            <FontAwesomeIcon icon={faExternalLinkAlt} className="ml-1 h-2.5 w-2.5" />
                                        </Link>
                                    ) : (
                                        <span className="ml-1 font-semibold text-gray-700">
                                            {highestAchievedDisplayText}
                                        </span>
                                    )}
                                 </div>
                             )}
                         </div>
                     </div>
                     {/* Right Side: Streak & Weekly Tracker */}
                     <div className="px-2 py-2 sm:px-4 sm:py-3 rounded-lg border border-gray-200 self-start sm:self-center bg-white shadow-sm hover:shadow-md transition-shadow duration-150 flex-shrink-0">
                          {/* Streak & Tracker content */}
                          <div className="flex items-center">
                             <div className="h-8 w-8 sm:h-9 sm:w-9 bg-amber-50 rounded-full flex items-center justify-center text-amber-500 mr-2">
                                 <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                     <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
                                 </svg>
                             </div>
                             <div className="mr-2 sm:mr-3">
                                 <div className="text-base sm:text-lg font-bold text-gray-900">{dailyStreak}</div>
                                 <div className="text-xs text-gray-500">Day Streak</div>
                             </div>
                             <div className="pl-1 sm:pl-2 border-l border-gray-100">
                                 <div className="flex items-center mb-1">
                                     <div className="text-[10px] sm:text-xs font-medium text-gray-700 flex items-center mr-1">
                                         <svg xmlns="http://www.w3.org/2000/svg" className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                         </svg>
                                         This Week:
                                     </div>
                                     <div className="text-[10px] sm:text-xs font-medium px-1 py-0.5 rounded-full bg-indigo-50 text-indigo-600 ml-auto">
                                         {getWeeklyCompletionCount(sessionsForComponents)}/7
                                     </div>
                                 </div>
                                 {/* Pass timezone to WeeklyTracker if it needs it */}
                                 <WeeklyTracker sessions={sessionsForComponents} timezone={userTimezone} />
                             </div>
                         </div>
                     </div>
                 </div>
                 {/* Focus Meter Card (Bottom Part) */}
                 <div className="bg-white p-3 sm:p-5 rounded-b-lg border border-t-0 border-gray-200 shadow-sm">
                     {/* Focus Meter content */}
                     <h3 className="text-xs sm:text-sm uppercase text-gray-500 font-semibold mb-2 sm:mb-3 flex items-center">
                         <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                         Current Progress
                     </h3>
                     <FocusMeter
                         difficulty={activeProfile.currentDifficulty}
                         nBack={activeProfile.currentNBack}
                         showAdvancementInfo={true}
                     />
                 </div>
            </div>

            {/* Action Hub: Start Training & Skip Challenge */}
            <div className="mb-5 sm:mb-8 grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                 {/* Play Button Card */}
                 <div className="bg-white p-3 sm:p-5 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-150 flex flex-col items-center justify-center">
                     {/* Play button content */}
                     <h2 className="text-base sm:text-lg font-semibold mb-2 text-gray-800">Start Training</h2>
                    <p className="text-xs sm:text-sm text-gray-600 text-center mb-3 sm:mb-4">
                      Begin your daily training session at your current level: <span className="font-semibold">{currentLevelDisplay}</span>.
                    </p>
                    <button
                        className="w-full bg-indigo-600 text-white py-2.5 sm:py-3 px-3 sm:px-5 rounded-lg hover:bg-indigo-700 font-medium text-sm sm:text-base transition duration-150 shadow-sm hover:shadow focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 flex items-center justify-center"
                        onClick={handleStandardPlay}
                        title={`Start standard ${currentNBackLevel}-back game at Level ${currentLevelDisplay}`}
                    >
                        <FontAwesomeIcon icon={faCirclePlay} className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                        <span>Start Training</span>
                    </button>
                 </div>
                 {/* Skip Challenge Card */}
                 <div className="bg-white p-3 sm:p-5 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-150 flex flex-col items-center justify-center">
                      {/* Skip challenge content */}
                       <h2 className="text-base sm:text-lg font-semibold mb-2 text-gray-800">Skip Level Challenge</h2>
                    <p className="text-xs sm:text-sm text-gray-600 text-center mb-3 sm:mb-4">
                      Attempt to skip directly to the next N-Back level ({challengeLevelDisplay}).
                    </p>
                    <button
                        className={`w-full py-2.5 sm:py-3 px-3 sm:px-5 rounded-lg font-medium text-sm sm:text-base transition-colors duration-150 flex items-center justify-center ${ canChallenge ? 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 shadow-sm hover:shadow focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500' : 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200' }`}
                        onClick={handleChallenge}
                        disabled={!canChallenge}
                        title={canChallenge ? `Try to skip ${currentNBackLevel}-back (attempt Level ${challengeLevelDisplay})` : `Already at max level (${MAX_NBACK})`}
                    >
                        <FontAwesomeIcon icon={faForward} className="mr-2 h-3.5 w-3.5 sm:h-4 sm:w-4" />
                        <span>Skip {currentNBackLevel}-back Challenge</span>
                    </button>
                 </div>
            </div>

            {/* Stats Section with Tabs */}
            <div className="mb-5 sm:mb-8">
                 <div className="flex items-center justify-between mb-3 sm:mb-4">
                      {/* Title */}
                      <h2 className="text-lg sm:text-xl font-semibold text-gray-800 flex items-center">
                         <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-gray-600" viewBox="0 0 20 20" fill="currentColor"><path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" /></svg>
                         Session Stats
                      </h2>
                      {/* Tab Buttons */}
                      <div className="flex space-x-1 bg-gray-100 p-0.5 rounded-lg">
                           {/* Today Button */}
                           <button
                              onClick={() => setActiveStatsTab('today')}
                              className={`px-3 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-colors duration-150 focus:outline-none ${
                                  activeStatsTab === 'today'
                                      ? 'bg-white text-indigo-700 shadow-sm'
                                      : 'text-gray-600 hover:bg-gray-200 hover:text-gray-800'
                              }`}
                          >
                              Today
                          </button>
                          {/* Week Button */}
                          <button
                              onClick={() => setActiveStatsTab('week')}
                              className={`px-3 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-colors duration-150 focus:outline-none ${
                                  activeStatsTab === 'week'
                                      ? 'bg-white text-indigo-700 shadow-sm'
                                      : 'text-gray-600 hover:bg-gray-200 hover:text-gray-800'
                              }`}
                          >
                              Week
                          </button>
                          {/* Month Button */}
                          <button
                              onClick={() => setActiveStatsTab('month')}
                              className={`px-3 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-colors duration-150 focus:outline-none ${
                                  activeStatsTab === 'month'
                                      ? 'bg-white text-indigo-700 shadow-sm'
                                      : 'text-gray-600 hover:bg-gray-200 hover:text-gray-800'
                              }`}
                          >
                              Month
                          </button>
                      </div>
                 </div>
                 {/* Conditional Rendering based on active tab */}
                 <div>
                     {/* *** Pass userTimezone to stats components *** */}
                     {activeStatsTab === 'today' && <TodaysStats sessions={sessionsForComponents} timezone={userTimezone} />}
                     {activeStatsTab === 'week' && <WeeklyStats sessions={sessionsForComponents} timezone={userTimezone} />}
                     {activeStatsTab === 'month' && <MonthlyStats sessions={sessionsForComponents} timezone={userTimezone}/>}
                 </div>
            </div>

            {/* Performance Comparison Chart Section */}
            <div className="mb-5 sm:mb-8">
                 <div className="flex items-center justify-between mb-3 sm:mb-4">
                     <h2 className="text-lg sm:text-xl font-semibold text-gray-800 flex items-center">
                         <FontAwesomeIcon icon={faChartLine} className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-gray-600" />
                         Performance Comparison
                     </h2>
                     {/* Performance Chart Period Buttons */}
                     <div className="flex space-x-1 bg-gray-100 p-0.5 rounded-lg">
                         <button
                             onClick={() => handlePerformanceChartPeriodChange('today')}
                             className={`px-3 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-colors duration-150 focus:outline-none ${
                                 performanceChartPeriod === 'today'
                                     ? 'bg-white text-indigo-700 shadow-sm'
                                     : 'text-gray-600 hover:bg-gray-200 hover:text-gray-800'
                             }`}
                         >
                             Today
                         </button>
                         <button
                             onClick={() => handlePerformanceChartPeriodChange('week')}
                             className={`px-3 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-colors duration-150 focus:outline-none ${
                                 performanceChartPeriod === 'week'
                                     ? 'bg-white text-indigo-700 shadow-sm'
                                     : 'text-gray-600 hover:bg-gray-200 hover:text-gray-800'
                             }`}
                         >
                             Week
                         </button>
                         <button
                             onClick={() => handlePerformanceChartPeriodChange('month')}
                             className={`px-3 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-colors duration-150 focus:outline-none ${
                                 performanceChartPeriod === 'month'
                                     ? 'bg-white text-indigo-700 shadow-sm'
                                     : 'text-gray-600 hover:bg-gray-200 hover:text-gray-800'
                             }`}
                         >
                             Month
                         </button>
                     </div>
                 </div>
                 {/* *** Pass the userTimezone prop *** */}
                 <DailyPerformanceChart
                     sessions={sessionsForComponents}
                     selectedPeriod={performanceChartPeriod}
                     timezone={userTimezone}
                  />
            </div>

            {/* Recent Sessions Table Section */}
            <div className="mb-5 sm:mb-8">
                 <div className="flex justify-between items-center mb-3 sm:mb-4">
                     <h2 className="text-lg sm:text-xl font-semibold text-gray-800 flex items-center">
                         <FontAwesomeIcon icon={faList} className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-gray-600" />
                         Recent Sessions
                     </h2>
                     <Link
                         to="/sessions" // Link to the new page
                         className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs sm:text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                         title="View all session history"
                     >
                         View All
                         <FontAwesomeIcon icon={faArrowRight} className="ml-1.5 h-3 w-3" />
                     </Link>
                 </div>
                 {/* *** Pass timezone here too for date display formatting *** */}
                 <SessionsTable
                     sessions={sessionsForComponents}
                     onNavigate={navigate}
                     timezone={userTimezone}
                 />
            </div>
        </div>
    );
};

export default ProfileDashboard;